import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flag, Share2, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useI18n } from "@/i18n";
import MediaImage from "@/components/MediaImage";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/seo";
import { CardsPanel, MatchesPanel, MilestonesPanel, NewsPanel, SignalPanel } from "@/components/PlayerPanels";
import { ShareCardSheet } from "@/components/ShareCardSheet";
import { MarketplacePanel } from "@/components/MarketplacePanel";
import { HighlightsLink } from "@/components/HighlightsLink";

export default function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { me } = useAuth();
  const [inList, setInList] = useState(false);
  const [market, setMarket] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    api.get(`/players/${id}`).then((r) => setData(r.data)).catch((e) => setError(e.message));
    api.get(`/cards/marketplace-links?lang=${lang}`).then((r) => {      const links = r.data.links || [];
      setMarkets(links);
      setMarket(links[0] || null);
    }).catch(() => {});
  }, [id, api, lang]);

  const premium = !!me && (me.premium_access);
  const seoPlayer = data?.player;
  useSeo(
    seoPlayer
      ? `${seoPlayer.name} — ${seoPlayer.club_name || ""}${seoPlayer.signal_score != null ? ` · Hype ${seoPlayer.signal_score}` : ""}${seoPlayer.status === "graduated" ? ` · ${t("player.seo_archive")}` : ""}`
      : null,
    seoPlayer ? t("player.seo_desc").replace("{name}", seoPlayer.name).replace("{club}", seoPlayer.club_name || "").replace("{league}", seoPlayer.league_name || "") : null,
    seoPlayer ? `/players/${seoPlayer.player_id}?p=${seoPlayer.provider || "api-football"}` : null,
  );

  useEffect(() => {
    if (me && data?.player) {
      api.get("/lists/ids").then((r) => {
        setInList((r.data.ids || []).includes(`${data.player.provider}:${data.player.player_id}`));
      }).catch(() => {});
    }
  }, [me, data, api]);

  const toggleList = async () => {
    if (!me) {
      navigate(`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    const p = data.player;
    const was = inList;
    setInList(!was); // immediate visual feedback; reverted if the call fails
    try {
      const r = await api.post("/lists/toggle", { player_id: p.player_id, provider: p.provider });
      setInList(r.data.in_list);
      toast.success(r.data.in_list ? t("player.list_added") : t("player.list_removed"));
    } catch (e) {
      setInList(was);
      if (e?.response?.data?.detail === "free_limit_reached") {
        toast(t("filters.free_limit_title", { n: 1 }), {
          description: t("filters.free_limit_upsell"),
          action: { label: t("hero.cta"), onClick: () => navigate("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(e?.response?.data?.detail || e.message);
      }
    }
  };

  const share = async () => {
    // An abbreviated name on a shared card looks amateurish, so the card is not offered
    // there at all (the endpoint refuses it too).
    setShareOpen(true);
  };

  if (error) {    return <p className="pt-16 text-center text-sm text-red-400" data-testid="player-error">{error}</p>;
  }
  if (!data) {
    return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;
  }
  const { player: p, milestones, signal, matches, news, cards } = data;
  const score = signal?.score ?? p.signal_score ?? null; // null = milestones never computed → dash, never a fabricated 0
  const reasons = score === null ? [] : (signal?.reasons?.length ? signal.reasons : [{ key: "no_signal", points: 0 }]);

  return (
    <div className="pt-6" data-testid="player-detail-page">
      <Link to="/" data-testid="back-to-watchlist" className="mb-4 inline-flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-wider text-slate-400 hover:text-[#00FF87]">
        <ArrowLeft className="h-3.5 w-3.5" /> {t("player.back")}
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl p-4 hud-card sm:p-5">
        <span className="flex shrink-0 items-center gap-2">
          {p.photo_url && (
            <MediaImage
              src={p.photo_url}
              alt={t("media.portrait_alt", { name: p.name })}
              kind="player" size={80} radius="rounded-xl" surface="player_page"
              testid="player-portrait"
            />
          )}
          <MediaImage
            src={p.club_logo}
            alt={t("media.logo_alt", { club: p.club_name || "—" })}
            kind="club" size={p.photo_url ? 48 : 80} radius="rounded-xl" surface="player_page"
            imgClassName="object-contain p-1.5"
            testid="player-club-crest"
          />
        </span>
        <div className="min-w-0 flex-1 basis-[12rem]">
          <h1 className="break-words font-display text-2xl font-black uppercase leading-tight tracking-tight text-slate-50 sm:text-4xl sm:leading-none" data-testid="player-name">
            {p.name}
          </h1>
          {p.name_complete === false && (
            <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-[#FFD600]/80" data-testid="player-name-incomplete">
              {t("player.name_incomplete")}
            </p>
          )}
          <p className="mt-1.5 text-sm text-slate-400">
            {p.position_group ? t(`pos.${p.position_group}`) : p.position || "—"} · <span data-testid="player-club-display">{p.club_name || "—"}{p.parent_club && p.parent_club !== p.club_name ? ` (${t("player.loan_from", { club: p.parent_club })})` : ""}</span> · {p.league_name}
          </p>
          <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
            {t("player.age")}: {p.age ?? "—"} · {t("player.country")}: {p.nationality || "—"}
            {p.apps_season != null && (
              <span data-testid="player-appearances"> · {p.apps_season === 1 ? t("player.appearances_season_one") : t("player.appearances_season", { n: p.apps_season })}</span>
            )}
            {p.minutes_season != null && <> · {t("player.minutes")}: {p.minutes_season}</>}
          </p>
          {(p.reserve_appearances || []).length > 0 && (
            <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid="player-reserve-appearances">
              {t("player.reserve_apps")}: {p.reserve_appearances.map((r) => `${r.team} ${r.apps} (${r.league})`).join(" · ")}
            </p>
          )}
        </div>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto">
        <button
          data-testid="player-add-to-list"
          onClick={toggleList}
          aria-pressed={inList}
          title={inList ? t("player.remove_hint") : t("player.add_to_list")}
          className={`flex w-full shrink-0 items-center justify-center gap-2 rounded-md border px-3 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider transition-colors sm:w-auto ${
            inList ? "border-[#FFD600]/60 bg-[#FFD600]/15 text-[#FFD600] hover:border-red-500/60 hover:text-red-400" : "border-[#1F293D] text-slate-300 hover:border-[#FFD600]/50 hover:text-[#FFD600]"
          }`}
        >
          <Star className={`h-3.5 w-3.5 ${inList ? "fill-current" : ""}`} />
          {inList ? t("player.in_list") : t("player.add_to_list")}
        </button>
        {/* Partage disponible pour TOUS les joueurs : le garde « nom abrégé » ne concerne
            que l'image de carte, pas le partage du lien de la fiche. */}
        <button
          data-testid="player-share-btn"
          onClick={share}
          title={t("player.share_hint")}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-[#1F293D] px-3 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF] sm:w-auto"
        >
          <Share2 className="h-3.5 w-3.5" />
          {t("player.share")}
        </button>
        <HighlightsLink playerName={p.name} clubName={p.club_name} nameComplete={p.name_complete} careerApps={p.rookie?.apps_basis} t={t} lang={lang} />
        {/* Jérôme, scout, a trouvé deux erreurs en deux minutes le 24/09/2026 : les gens qui
            connaissent ces joueurs doivent pouvoir nous les signaler depuis la fiche. */}
        <Link
          to={`/feedback?player=${p.player_id}&p=${p.provider || "api-football"}&name=${encodeURIComponent(p.name || "")}`}
          data-testid="player-report-error"
          className="flex w-full items-center justify-center gap-2 rounded-md border border-[#1F293D] px-3 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-slate-400 transition-colors hover:border-[#FFD600]/60 hover:text-[#FFD600] sm:w-auto"
        >
          <Flag className="h-3.5 w-3.5" />
          {t("player.report_error")}
        </Link>
        </div>
      </div>

      <ShareCardSheet playerId={p.player_id} playerName={p.name} open={shareOpen} onClose={() => setShareOpen(false)} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section className="lg:col-span-5" data-testid="player-signal-panel">
          <SignalPanel score={score} reasons={reasons} signal={signal} series={data.signal_series} delta7d={data.signal_delta_7d} locked={data.trend_locked} noScoreReason={data.no_score_reason} t={t} lang={lang} />
          <NewsPanel news={news} t={t} lang={lang} />
          <MatchesPanel matches={matches} t={t} lang={lang} />
        </section>

        <section className="lg:col-span-7" data-testid="player-milestones-section">
          <CardsPanel cards={cards} t={t} market={market} />
          <MarketplacePanel links={markets} playerName={p.name} cardName={cards?.[0]?.player_name_raw} nameComplete={p.name_complete} setName={cards?.[0]?.set_name} t={t} />
          <MilestonesPanel milestones={milestones} t={t} lang={lang} />
        </section>
      </div>
    </div>
  );
}
